'use client'

import { useState } from 'react'
import { Button, Card, Col, Icon, Row, Typography } from '@repo/ui/components'
import { useTranslate } from '@repo/shared/hooks'
import { PublicProfileContainerProps } from './public-profile.types'
import Image from 'next/image'
import { useAgentAuthDetails, useGetAgentReferralCodes } from '@repo/services'
import { globalConfig } from '@repo/shared/utils'
import { EditProfileModal } from './edit-profile'
import { EditProfileFormData } from './edit-profile/edit-profile.types'
import { ButtonLink } from '@repo/shared'
import { EditProfileCoverModal } from './edit-profile/edit-profile-cover.modal'

export const PublicProfileContainer = ({
  onEditProfile,
  onShare,
  onViewAsOwner,
}: PublicProfileContainerProps) => {
  const { t } = useTranslate()
  const { data: authDetails } = useAgentAuthDetails()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isEditCoverModalOpen, setIsEditCoverModalOpen] = useState(false)
  const { data } = useGetAgentReferralCodes()
  const referralCode = data?.data?.codes?.[0]?.code
  const profileData = {
    name: authDetails?.data.details?.displayName,
    username: authDetails?.data.username,
    company: authDetails?.data.details?.agentInfo?.agencyName,
    address: authDetails?.data.details?.agentInfo?.agencyAddress,
    socialLinks: {
      instagram: authDetails?.data.details?.socials?.platforms?.find(
        (platform) => platform.platform === 'instagram',
      )?.link,
      whatsapp: authDetails?.data.details?.socials?.platforms?.find(
        (platform) => platform.platform === 'whatsapp',
      )?.link,
      telegram: authDetails?.data.details?.socials?.platforms?.find(
        (platform) => platform.platform === 'telegram',
      )?.link,
    },
    profileImage: authDetails?.data.details?.photo
      ? `${globalConfig.baseImageUrl}${authDetails?.data.details?.photo?.largeURL}`
      : '/images/placeholder.webp',
    coverImage: authDetails?.data.details?.coverPhoto
      ? `${globalConfig.baseImageUrl}${authDetails?.data.details?.coverPhoto?.url}`
      : '/images/cover-image.png',
  }

  const handleEditProfile = () => {
    setIsEditModalOpen(true)
  }

  const handleEditProfileSubmit = (data: EditProfileFormData) => {
    // TODO: Implement profile update logic
    console.log('Profile update data:', data)
    if (onEditProfile) {
      onEditProfile()
    }
  }

  const handleImageChange = (file: File) => {
    // TODO: Implement image upload logic
    console.log('New image file:', file)
  }

  const handleImageDelete = () => {
    // TODO: Implement image deletion logic
    console.log('Delete image')
  }

  const handleShare = async () => {
    const shareUrl = `${globalConfig.originWebUrl}/${authDetails?.data.username ? `u/${authDetails?.data.username}` : `uid/${authDetails?.data.uuid}`}/aff/${referralCode}`
    if (navigator.share) {
      try {
        await navigator.share({
          text: t('publicProfile_share_text', {
            url: shareUrl,
          }),
          // url: shareUrl,
        })
      } catch (error) {
        console.error('Error sharing:', error)
      }
    } else {
      // alert('Sharing is not supported in this browser.')
    }
  }

  const handleEditCoverProfile = () => {
    setIsEditCoverModalOpen(true)
  }

  return (
    <Card className='w-full mx-auto'>
      {/* Header */}
      <Row className='justify-between items-center'>
        <Typography size='md' weight='bold'>
          {t('publicProfile_title')}
        </Typography>
        <ButtonLink
          href={
            authDetails?.data.username
              ? `${globalConfig.originWebUrl}/u/${authDetails?.data.username}`
              : `${globalConfig.originWebUrl}/uid/${authDetails?.data.uuid}`
          }
          variant='ghost'
          target='_blank'
          size='md'
          color='base'
          startIcon={<Icon name='ArrowUpRight' size='sm' />}
          onClick={onViewAsOwner}
        >
          {t('publicProfile_viewAsOwner')}
        </ButtonLink>
      </Row>
      <div className='dark relative bg-neutral-surface-light rounded-lg overflow-hidden'>
        {/* Banner and Profile Image */}
        <div className='relative'>
          {/* Banner Image */}
          <div className='h-48 bg-gradient-to-br from-neutral-surface-60 to-neutral-surface-80 relative overflow-hidden'>
            <div
              className='absolute inset-0  bg-cover bg-center opacity-80'
              style={{ backgroundImage: `url(${profileData.coverImage})` }}
            />
          </div>

          {/* Profile Image */}
          <div className='absolute -bottom-12 left-1/2 transform -translate-x-1/2'>
            <div className='w-24 h-24 rounded-full bg-neutral-surface-100 border-4 border-base-main overflow-hidden'>
              <Image
                src={profileData.profileImage}
                width={120}
                height={120}
                alt='Profile Image'
                className='w-full h-full object-cover'
              />
            </div>
          </div>

          <Button
            variant='ghost'
            size='md'
            color='base'
            startIcon={<Icon name='PencilWithALine' size='sm' />}
            onClick={handleEditCoverProfile}
            className='absolute top-2 left-2'
          >
            {t('publicProfile_editCoverProfile')}
          </Button>
        </div>

        {/* Profile Information */}
        <Col className='pt-xl pb-lg px-lg text-center mt-5xl gap-sm'>
          <Typography size='lg' weight='bold' color='text-neutral-surface-100'>
            {profileData.name}
          </Typography>
          {profileData.username && (
            <Typography
              size='lg'
              weight='bold'
              color='text-neutral-surface-100'
              className='ltr'
            >
              @{profileData.username}
            </Typography>
          )}
          <Typography size='md' weight='bold' color='text-neutral-surface-100'>
            {profileData.company}
          </Typography>
          <Typography size='sm' color='text-neutral-surface-60'>
            {profileData.address}
          </Typography>

          {/* Social Links */}
          <Row className='justify-center gap-lg mb-lg'>
            {profileData.socialLinks.instagram && (
              <Col className='items-center gap-2xs'>
                <Icon name='Instagram' size='lg' className='text-purple-500' />
                <Typography
                  variant='caption'
                  className='text-neutral-surface-100'
                >
                  {profileData.socialLinks.instagram}
                </Typography>
              </Col>
            )}
            {profileData.socialLinks.whatsapp && (
              <Col className='items-center gap-2xs'>
                <Icon
                  name='WhatsappFill'
                  size='lg'
                  className='text-green-500'
                />
                <Typography
                  variant='caption'
                  className='text-neutral-surface-100'
                >
                  {profileData.socialLinks.whatsapp}
                </Typography>
              </Col>
            )}
            {profileData.socialLinks.telegram && (
              <Col className='items-center gap-2xs'>
                <Icon name='TelegramFill' size='lg' className='text-blue-500' />
                <Typography
                  variant='caption'
                  className='text-neutral-surface-100'
                >
                  {profileData.socialLinks.telegram}
                </Typography>
              </Col>
            )}
          </Row>

          {/* Action Buttons */}
          <div className=' flex flex-col  md:flex-row gap-md'>
            <Button
              variant='fill'
              size='md'
              color='base'
              width='full'
              startIcon={<Icon name='ShareLine' size='sm' />}
              onClick={handleShare}
            >
              {t('publicProfile_share')}
            </Button>
            <Button
              variant='ghost'
              size='md'
              color='base'
              width='full'
              startIcon={<Icon name='PencilWithALine' size='sm' />}
              onClick={handleEditProfile}
            >
              {t('publicProfile_editProfile')}
            </Button>
          </div>
        </Col>
      </div>

      {/* Edit Profile Modal */}
      {isEditModalOpen && (
        <EditProfileModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          onSubmit={handleEditProfileSubmit}
          initialData={{
            fullName: profileData.name || '',
            username: profileData.username || '',
            agencyName: profileData.company || '',
            agencyAddress: profileData.address || '',
            instagram: profileData.socialLinks.instagram || '',
            telegram: profileData.socialLinks.telegram || '',
            whatsapp: profileData.socialLinks.whatsapp || '',
          }}
          profileImage={authDetails?.data.details?.photo?.smallURL}
          coverImage={authDetails?.data.details?.photo?.largeURL}
          onImageChange={handleImageChange}
          onImageDelete={handleImageDelete}
        />
      )}

      {isEditCoverModalOpen && (
        <EditProfileCoverModal
          open={isEditCoverModalOpen}
          onOpenChange={setIsEditCoverModalOpen}
          coverImage={authDetails?.data.details?.coverPhoto?.url}
        />
      )}
    </Card>
  )
}
